/**
 * Admin Dashboard API client.
 * Fetches funnel, channel, onboarding, and checkout data from admin endpoints.
 */

import { apiGet, apiPost } from '@/services/apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface FunnelStats {
  account_created: number;
  login_completed: number;
  onboarding_started: number;
  onboarding_completed: number;
  carousel_created: number;
  carousel_edited: number;
  carousel_exported: number;
  checkout_initiated: number;
  checkout_form_started: number;
  checkout_form_completed: number;
  payment_approved: number;
  payment_failed: number;
  refund_requested: number;
}

export interface OverviewStats {
  accountsCreated: number;
  carouselsCreated: number;
  checkoutsInitiated: number;
  paymentsApproved: number;
  revenue: number;
  ticketMedio: number;
  conversionRate: number;
  funnel: FunnelStats;
}

export interface ChannelRow {
  channel: string;
  accounts: number;
  carousels: number;
  checkouts: number;
  payments: number;
  revenue: number;
}

export interface StepCount {
  step: string;
  users: number;
}

export interface RevenueRow {
  plan_name: string;
  period: string;
  channel: string;
  total_revenue: number;
  payment_count: number;
}

export interface CohortRow {
  cohort_week: string;
  total_users: number;
  activated: number;
  checkout_initiated: number;
  paid: number;
  revenue: number;
}

export interface DailyEventCount {
  day: string;
  event_name: string;
  count: number;
}

export interface RawEvent {
  id: string;
  user_id: string;
  event_name: string;
  metadata: Record<string, any>;
  created_at: string;
  email: string;
}

export interface AvgTime {
  avg_hours: number;
  median_hours: number;
  sample_size: number;
}

export interface RegisteredChannel {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TrackingLink {
  id: string;
  channel_id: string;
  label: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  influencer_id: string;
  coupon_code: string;
  full_url: string;
  is_active: boolean;
  created_at: string;
}

export interface CampaignRow {
  utm_campaign: string;
  utm_content: string;
  ad_creative_id: string;
  accounts: number;
  carousels: number;
  checkouts: number;
  payments: number;
  revenue: number;
}

export interface InfluencerRow {
  influencer_id: string;
  accounts: number;
  carousels: number;
  checkouts: number;
  payments: number;
  revenue: number;
}

export interface UtmBreakdownRow {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  derived_channel: string;
  accounts: number;
}

export interface LeadDetail {
  user_id: string;
  email: string;
  display_name: string;
  handle: string;
  phone: string;
  avatar_url: string;
  created_at: string;
  onboarding_completed: boolean;
  onboarding_data: {
    objetivo_carrosseis: string;
    tipo_conteudo: string;
    leads_instagram: string;
    faturamento: string;
  };
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  referrer: string;
  landing_page: string;
  first_touch_channel: string;
  device_type: string;
  influencer_id: string;
  coupon_code: string;
  country: string;
  language: string;
  subscription: {
    plan_name: string;
    plan_slug: string;
    status: string;
    period: string;
    started_at: string;
    expires_at: string;
  } | null;
  created_carousel: boolean;
  has_paid: boolean;
  cancellation: {
    reason: string;
    feedback: string;
    created_at: string;
  } | null;
  events: Array<{
    id: string;
    event_name: string;
    metadata: Record<string, any>;
    created_at: string;
  }>;
}

// ============================================================================
// API CALLS
// ============================================================================

export const getDashboardOverview = (period: string) =>
  apiGet<OverviewStats>('/api/admin/dashboard/overview', { period });

export const getDashboardFunnel = (period: string, channel?: string) =>
  apiGet<FunnelStats>('/api/admin/dashboard/funnel', { period, ...(channel ? { channel } : {}) });

export const getDashboardChannels = (period: string) =>
  apiGet<{ channels: ChannelRow[] }>('/api/admin/dashboard/channels', { period });

export const getDashboardOnboarding = (period: string, channel?: string) =>
  apiGet<{ steps: StepCount[] }>('/api/admin/dashboard/onboarding', { period, ...(channel ? { channel } : {}) });

export const getDashboardCheckout = (period: string) =>
  apiGet<{ steps: StepCount[] }>('/api/admin/dashboard/checkout', { period });

export const getDashboardDaily = (period: string) =>
  apiGet<{ data: DailyEventCount[] }>('/api/admin/dashboard/daily', { period });

export const getDashboardRevenue = (period: string) =>
  apiGet<{ data: RevenueRow[] }>('/api/admin/dashboard/revenue', { period });

export const getDashboardCohorts = (period: string) =>
  apiGet<{ data: CohortRow[] }>('/api/admin/dashboard/cohorts', { period });

export const getDashboardEvents = (page: number, pageSize: number, eventName?: string) => {
  const params: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
  if (eventName) params.eventName = eventName;
  return apiGet<{ events: RawEvent[]; total: number; page: number; pageSize: number }>('/api/admin/dashboard/events', params);
};

export const getDashboardAvgTime = (period: string, eventA: string, eventB: string) =>
  apiGet<AvgTime>('/api/admin/dashboard/avg-time', { period, eventA, eventB });

export const trackFunnelEvent = (eventName: string, metadata?: Record<string, any>) =>
  apiPost<{ success: boolean }>('/api/events/track', { event_name: eventName, metadata });

// ============================================================================
// CHANNEL & TRACKING LINK MANAGEMENT
// ============================================================================

export const getChannelsList = () =>
  apiGet<{ channels: RegisteredChannel[] }>('/api/admin/channels');

export const createChannelApi = (name: string, type: string) =>
  apiPost<RegisteredChannel>('/api/admin/channels/create', { name, type });

export const updateChannelApi = (id: string, updates: { name?: string; type?: string; is_active?: boolean }) =>
  apiPost<RegisteredChannel>('/api/admin/channels/update', { id, ...updates });

export const getTrackingLinks = (filters?: { channel_id?: string; utm_campaign?: string }) =>
  apiGet<{ links: TrackingLink[] }>('/api/admin/tracking-links', filters as Record<string, string>);

export const createTrackingLinkApi = (data: {
  channel_id?: string;
  channel_name?: string;
  channel_type?: string;
  label: string;
  base_url?: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  influencer_id?: string;
  coupon_code?: string;
}) => apiPost<TrackingLink>('/api/admin/tracking-links/create', data);

export const deleteTrackingLinkApi = (id: string) =>
  apiPost<{ success: boolean }>('/api/admin/tracking-links/delete', { id });

// ============================================================================
// CAMPAIGN & INFLUENCER
// ============================================================================

export const getDashboardCampaigns = (period: string, channel: string) =>
  apiGet<{ data: CampaignRow[] }>('/api/admin/dashboard/campaigns', { period, channel });

export const getDashboardInfluencers = (period: string) =>
  apiGet<{ data: InfluencerRow[] }>('/api/admin/dashboard/influencers', { period });

export const getDashboardUtmBreakdown = (period: string) =>
  apiGet<{ data: UtmBreakdownRow[] }>('/api/admin/dashboard/utm-breakdown', { period });

export const getLeadDetail = (userId: string) =>
  apiGet<LeadDetail>('/api/admin/dashboard/lead-detail', { userId });
